import { useEffect, useState } from "react";
import { Outlet, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AlphaCoreLogo from "@/components/AlphaCoreLogo";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Shield, Users, DollarSign, BarChart3, MessageCircle, ArrowLeft, LayoutDashboard, Film, Layers, TrendingUp, Menu, Sparkles } from "lucide-react";

const NAV = [
  { to: "/admin", icon: Shield, label: "Overview", end: true },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/analytics", icon: TrendingUp, label: "Analytics" },
  { to: "/admin/modules", icon: Layers, label: "Modules" },
  { to: "/admin/revenue", icon: DollarSign, label: "Revenue" },
  { to: "/admin/payments", icon: DollarSign, label: "Payments" },
  { to: "/admin/plan-applications", icon: Sparkles, label: "Plan Applications" },
  { to: "/admin/video-access", icon: Film, label: "Video Access" },
  { to: "/admin/usage", icon: BarChart3, label: "API Usage" },
  { to: "/admin/video-editor", icon: Film, label: "Video Editor" },
  { to: "/admin/tickets", icon: MessageCircle, label: "Support" },
];

function Nav({ onNavigate }) {
  return (
    <nav className="flex-1 py-5 px-3 space-y-0.5">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors min-h-[44px] ${
              isActive ? "bg-[#8A52FF]/10 text-[#8A52FF]" : "text-white/55 hover:text-white hover:bg-white/[0.03]"
            }`
          }
          data-testid={`admin-nav-${item.label.toLowerCase()}`}
        >
          <item.icon className="h-4 w-4" strokeWidth={1.6} /> {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  if (!user) return null;
  if (user.role !== "admin" && user.role !== "super_admin") return <Navigate to="/dashboard" replace />;

  const currentLabel = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label || "Admin";

  return (
    <div className="app-shell bg-[#050505] text-white flex overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 h-full min-h-0 overflow-y-auto border-r border-white/5 flex-col" data-testid="admin-sidebar">
        <div className="h-16 flex items-center px-4 border-b border-white/5 gap-2">
          <AlphaCoreLogo size={36} />
          <div className="text-sm">
            <div className="font-display tracking-tight leading-none">Admin</div>
            <div className="kr-overline mt-0.5">PANEL</div>
          </div>
        </div>
        <Nav />
        <div className="border-t border-white/5 p-3">
          <button onClick={() => navigate("/dashboard")} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/50 hover:text-white hover:bg-white/[0.03] rounded-sm min-h-[44px]" data-testid="admin-back-to-app">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to app
          </button>
        </div>
      </aside>

      {/* Mobile Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[86vw] max-w-[340px] p-0 bg-[#050505] border-r border-white/10 text-white flex flex-col" data-testid="admin-mobile-drawer">
          <SheetTitle className="sr-only">Admin navigation</SheetTitle>
          <SheetDescription className="sr-only">Admin panel primary menu.</SheetDescription>
          <div className="h-14 flex items-center px-4 border-b border-white/5">
            <div className="flex items-center gap-2">
              <AlphaCoreLogo size={30} />
              <div className="kr-overline">[ ADMIN ]</div>
            </div>
          </div>
          <Nav onNavigate={() => setDrawerOpen(false)} />
          <div className="border-t border-white/5 p-3">
            <button onClick={() => navigate("/dashboard")} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/[0.03] rounded-sm min-h-[44px]">
              <ArrowLeft className="h-4 w-4" /> Back to app
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Desktop topbar */}
        <header className="hidden lg:flex h-16 border-b border-white/5 px-6 items-center justify-between kr-glass">
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Shield className="h-4 w-4 text-[#8A52FF]" />
            <span className="kr-overline">[ ADMIN MODE ]</span>
          </div>
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-xs text-white/50 hover:text-white" data-testid="admin-topbar-app-btn">
            <LayoutDashboard className="h-3.5 w-3.5" /> Switch to user view
          </button>
        </header>

        {/* Mobile topbar */}
        <header className="lg:hidden h-14 border-b border-white/5 px-3 flex items-center justify-between kr-glass sticky top-0 z-30 pt-[env(safe-area-inset-top)]" data-testid="admin-mobile-topbar">
          <button onClick={() => setDrawerOpen(true)} className="p-2.5 -ml-1 rounded-md hover:bg-white/5 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Open admin menu" data-testid="admin-mobile-hamburger">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0 flex-1 justify-center">
            <Shield className="h-4 w-4 text-[#8A52FF] shrink-0" />
            <span className="text-sm font-display font-semibold truncate">{currentLabel}</span>
          </div>
          <button onClick={() => navigate("/dashboard")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-white/50 hover:text-white" aria-label="Exit admin">
            <LayoutDashboard className="h-4 w-4" />
          </button>
        </header>

        <main className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
